import { buttonVariants } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import {
  MonthCaptionProps,
  type CustomComponents,
  useDayPicker,
} from "react-day-picker";
import { CALENDAR_MONTH_NAMES, getCalendarYears } from "./calendar-helpers";

export type CalendarCaptionMode = "default" | "dropdowns";

type CaptionComponents = Partial<CustomComponents>;
type DayPickerChevronProps = Parameters<
  NonNullable<CaptionComponents["Chevron"]>
>[0];

function MonthCaptionWithDropdowns(
  props: MonthCaptionProps & { fromYear?: number; toYear?: number },
) {
  const { goToMonth } = useDayPicker();
  const { fromYear = 1900, toYear = 2100 } = props;

  const currentMonth = props.calendarMonth.date;
  const currentYear = currentMonth.getFullYear();
  const currentMonthIndex = currentMonth.getMonth();
  const years = getCalendarYears(fromYear, toYear);

  const handleMonthChange = (month: string) => {
    goToMonth(new Date(currentYear, Number.parseInt(month, 10)));
  };

  const handleYearChange = (year: string) => {
    goToMonth(new Date(Number.parseInt(year, 10), currentMonthIndex));
  };

  return (
    <div className="mb-2 flex justify-center gap-2">
      <Select value={currentMonthIndex.toString()} onValueChange={handleMonthChange}>
        <SelectTrigger className="h-8 w-[130px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CALENDAR_MONTH_NAMES.map((month, index) => (
            <SelectItem key={month} value={index.toString()}>
              {month}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={currentYear.toString()} onValueChange={handleYearChange}>
        <SelectTrigger className="h-8 w-[100px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-[200px]">
          {[...years].reverse().map((year) => (
            <SelectItem key={year} value={year.toString()}>
              {year}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DefaultMonthCaption(props: MonthCaptionProps) {
  const { calendarMonth, displayIndex, className, ...rest } = props;
  const { goToMonth, previousMonth, nextMonth, labels, months } = useDayPicker();
  const isFirstMonth = displayIndex === 0;
  const isLastMonth = displayIndex === months.length - 1;

  return (
    <div className={cn("flex h-9 items-center justify-center gap-1", className)} {...rest}>
      {isFirstMonth ? (
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: "outline" }),
            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
          )}
          onClick={() => previousMonth && goToMonth(previousMonth)}
          disabled={!previousMonth}
          aria-label={labels.labelPrevious(previousMonth)}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      ) : (
        <span className="h-7 w-7" aria-hidden />
      )}
      <span className="text-sm font-medium">{format(calendarMonth.date, "MMMM yyyy")}</span>
      {isLastMonth ? (
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: "outline" }),
            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
          )}
          onClick={() => nextMonth && goToMonth(nextMonth)}
          disabled={!nextMonth}
          aria-label={labels.labelNext(nextMonth)}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      ) : (
        <span className="h-7 w-7" aria-hidden />
      )}
    </div>
  );
}

export function getCalendarComponents({
  captionMode,
  fromYear,
  toYear,
}: {
  captionMode: CalendarCaptionMode;
  fromYear?: number;
  toYear?: number;
}): CaptionComponents {
  const baseComponents: CaptionComponents = {
    Chevron: ({
      className,
      orientation,
      size,
      disabled: _disabled,
      ...props
    }: DayPickerChevronProps) => (
      <ChevronRight
        size={size ?? 16}
        className={cn(
          "h-4 w-4",
          orientation === "left" && "rotate-180",
          orientation === "up" && "-rotate-90",
          orientation === "down" && "rotate-90",
          className,
        )}
        {...props}
      />
    ),
    MonthCaption: (captionProps: MonthCaptionProps) => (
      <DefaultMonthCaption {...captionProps} />
    ),
  };

  if (captionMode === "dropdowns") {
    return {
      ...baseComponents,
      MonthCaption: (captionProps: MonthCaptionProps) => (
        <MonthCaptionWithDropdowns
          {...captionProps}
          fromYear={fromYear}
          toYear={toYear}
        />
      ),
    };
  }

  return baseComponents;
}
